import { IndexedDBKv, KvCache, MemStorage, StorageKv, type KvDb } from "@package/util/kv";
import { logger, type Tag } from "@artis/logger";

import { FOLDER_ACCESS_VALUES, type FolderAccess, type FolderAccessBaseline, type FolderSnapshotStore } from "./index";

type PersistedSnapshot = { userId: string; folders: [string, FolderAccess][] };

type Items = { "folder-access": PersistedSnapshot; "permission-table": PersistedSnapshot };

type SnapshotKey = keyof Items;

/** Dropped if untouched for this long — stale access is worse than no comparison at all. */
const MAX_IDLE_MS = 30 * 24 * 3600_000;

/** Bumped when the stored shape changes: a mismatch is ignored, which just re-baselines. */
const VERSION = 2;

const isAccess = (value: unknown): value is FolderAccess => FOLDER_ACCESS_VALUES.includes(value as FolderAccess);

/** Persisted data is untrusted: a shape change must not break alerting, only reset the baseline. */
const parse = (persisted: PersistedSnapshot | null, userId: string): FolderAccessBaseline | null => {
  if (!persisted || persisted.userId !== userId || !Array.isArray(persisted.folders)) return null;

  const baseline: FolderAccessBaseline = new Map();
  const dropped: unknown[] = [];
  for (const pair of persisted.folders) {
    if (!Array.isArray(pair) || typeof pair[0] !== "string" || !isAccess(pair[1])) {
      dropped.push(Array.isArray(pair) ? pair[1] : pair);
      continue;
    }
    baseline.set(pair[0], pair[1]);
  }

  // A dropped folder reads back as "none", which detection reports as freshly gained access on
  // every boot, so this should never happen for a level the app actually hands out.
  if (dropped.length > 0) {
    logger.warn("Discarded unreadable folder access levels from snapshot", {
      tags: ["calc-worker"],
      dropped,
      kept: baseline.size,
    });
    if (baseline.size === 0) return null;
  }

  return baseline;
};

const createAccessSnapshotStore = (key: SnapshotKey, db?: KvDb<Record<string, unknown>>): FolderSnapshotStore => {
  const kv = db ?? (typeof indexedDB !== "undefined" ? new IndexedDBKv() : new StorageKv(new MemStorage()));
  const cache = new KvCache<Items>(kv, { version: VERSION, maxIdleTime: MAX_IDLE_MS });
  const tags: Tag[] = key === "folder-access" ? ["calc-worker"] : ["webapp"];

  return {
    load: async (userId) => {
      try {
        const baseline = parse(await cache.get(key), userId);
        // Without a baseline the next tick becomes the reference point, so nothing is reported;
        // with one, anything that moved since it was written is reported as a change.
        logger.info("Restored folder access snapshot", { tags, key, folders: baseline?.size ?? 0 });
        return baseline;
      } catch (error) {
        logger.warn("Failed to read persisted folder access snapshot", { tags, key, error });
        return null;
      }
    },
    save: async (userId, baseline) => {
      try {
        await cache.set(key, { userId, folders: [...baseline] });
        logger.info("Persisted folder access snapshot", { tags, key, folders: baseline.size });
      } catch (error) {
        logger.warn("Failed to persist folder access snapshot", { tags, key, error });
      }
    },
  };
};

/**
 * Remembers folder access across worker restarts so a reload — or a permission change made while
 * the tab was closed — is still reported rather than being absorbed into a fresh baseline. Only
 * package ids and access levels are stored; folder and curve names never reach disk.
 */
export const createFolderSnapshotStore = (db?: KvDb<Record<string, unknown>>): FolderSnapshotStore =>
  createAccessSnapshotStore("folder-access", db);

export const createPermissionTableSnapshotStore = (db?: KvDb<Record<string, unknown>>): FolderSnapshotStore =>
  createAccessSnapshotStore("permission-table", db);
